/**
 * Keyed in-memory TTL cache with in-flight request coalescing. Concurrent callers for the same
 * key share a single fetch; the result populates the cache for the configured TTL. Entries expire
 * by wall-clock time but are not actively evicted — `clear()` is the only way to drop state.
 *
 * Designed for client-side wrapping of HTTP fetches that change rarely (15-min-ish TTL). For
 * unkeyed caches, use a fixed sentinel like `"all"` as the key.
 */

interface Entry<V> {
	data: V;
	fetchedAt: number;
}

export interface TtlCacheOpts {
	/** Override now() for tests. */
	now?: () => number;
}

export class TtlCache<K, V> {
	private readonly entries = new Map<K, Entry<V>>();
	private readonly inflight = new Map<K, Promise<V>>();
	private readonly ttlMs: number;
	private readonly fetcher: (key: K) => Promise<V>;
	private readonly now: () => number;

	constructor(ttlMs: number, fetcher: (key: K) => Promise<V>, opts: TtlCacheOpts = {}) {
		this.ttlMs = ttlMs;
		this.fetcher = fetcher;
		this.now = opts.now ?? Date.now;
	}

	/** Synchronous cache hit, or null if missing/expired. */
	peek(key: K): V | null {
		const entry = this.entries.get(key);
		if (entry && this.now() - entry.fetchedAt < this.ttlMs) return entry.data;
		return null;
	}

	/** Cache hit, in-flight join, or fresh fetch (in that priority). */
	async get(key: K): Promise<V> {
		const cached = this.peek(key);
		if (cached !== null) return cached;
		const existing = this.inflight.get(key);
		if (existing) return existing;
		const promise = (async () => {
			try {
				const data = await this.fetcher(key);
				this.entries.set(key, { data, fetchedAt: this.now() });
				return data;
			} finally {
				this.inflight.delete(key);
			}
		})();
		this.inflight.set(key, promise);
		return promise;
	}

	/** Drop all cached entries and in-flight requests. */
	clear(): void {
		this.entries.clear();
		this.inflight.clear();
	}
}
